import {
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import Header from "components/Header";
import WidgetWrapper from "components/WidgetWrapper";
import React from "react";

import "dayjs/locale/en-gb";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import { useCoaching } from "./hooks/useCoaching";
import UserForm from "pages/admin/UserAdmin/Components/UserForm";

function AssesseeTable() {
  const {
    backgroundAlt,
    primaryLight,
    primaryDark,
    isLargeScreen,
    showOrg,
    showDivision,
    showLevel,
    showSeniorLeader,
    isAcademyImprovementLead,
    searchInput,
    setSearchInput,
    selectedLocation,
    setSelectedLocation,
    selectedDivision,
    setSelectedDivision,
    selectedLevel,
    setSelectedLevel,
    selectedAssessor,
    setSelectedAssessor,
    selectedAssessee,
    setSelectedAssessee,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    locations,
    divisions,
    levels,
    assessors,
    assesseeNames,
    pageSize,
    setPageSize,
    page,
    setPage,
    search,
    setSearch,
    sortModel,
    setSortModel,
    filterModel,
    setFilterModel,
    sort,
    setSort,
    rows,
    totalRows,
    columns,
    openUserForm,
    setOpenUserForm,
    currentUserName,
    handleCloseUserForm,
    setNewUserId,
  } = useCoaching();

  return (
    <Box m={isLargeScreen ? "1rem" : "1.5rem 0.5rem 2rem 0.5rem"}>
      <WidgetWrapper>
        <Header
          title="ALT Coach - Coaching Table"
          subtitle="View all assessees assessments"
        />

        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={3}>
          {(showOrg || isAcademyImprovementLead) && (
            <TextField
              select
              label="School"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc._id} value={loc._id}>
                  {loc.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showLevel || showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Department"
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Divisions</MenuItem>
              {divisions.map((div) => (
                <MenuItem key={div._id} value={div._id}>
                  {div.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(showDivision || showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Year"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              {levels.map((lvl) => (
                <MenuItem key={lvl._id} value={lvl._id}>
                  {lvl.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            select
            label="Assessor"
            value={selectedAssessor}
            onChange={(e) => setSelectedAssessor(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All Assessors</MenuItem>
            {assessors.map((assessor) => (
              <MenuItem key={assessor._id} value={assessor._id}>
                {assessor.firstName} {assessor.lastName}
              </MenuItem>
            ))}
          </TextField>

          {/* New Assessee Dropdown */}
          {(showSeniorLeader || isAcademyImprovementLead) && (
            <TextField
              select
              label="Assessee"
              value={selectedAssessee}
              onChange={(e) => setSelectedAssessee(e.target.value)}
              sx={{ minWidth: 200 }}
              size="small"
            >
              <MenuItem value="">All Assessments</MenuItem>
              {assesseeNames.map((name, index) => (
                <MenuItem key={index} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box
            display="flex"
            gap={2}
            alignItems="center"
            sx={{ minWidth: 200 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small", sx: { minWidth: 200 } },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Box
          mt="40px"
          sx={{
            "& .MuiDataGrid-root": {},
            "& .MuiDataGrid-cell": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflow: "visible",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: backgroundAlt,
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: backgroundAlt,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: primaryLight,
            },
            "& .MuiDataGrid-row": {
              height: "auto", // This ensures the row height adapts to content
            },
          }}
        >
          <DataGrid
            // getRowId={(row) => row._id}
            rows={rows}
            columns={columns}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalRows}
            rowsPerPageOptions={[10, 25, 50, 100]}
            pagination
            paginationMode="server"
            autoHeight
            rowHeight={50}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize) => {
              setPage(1);
              setPageSize(newPageSize);
            }}
            filterMode="server"
            filterModel={filterModel}
            onFilterModelChange={(newFilterModel) => {
              setPage(1);
              setFilterModel(newFilterModel);
            }}
            components={{
              Toolbar: DataGridCustomToolbar,
            }}
            componentsProps={{
              toolbar: {
                search,
                setSearch,
                searchInput,
                setSearchInput,
                sort,
                setSort,
                backgroundAlt,
                primaryLight,
                primaryDark,
              },
            }}
          />
        </Box>
        {/* The Dialog component is a modal dialog that is displayed over the current page. */}
        <Dialog
          open={openUserForm}
          onClose={handleCloseUserForm}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add a New User to your Organisation</DialogTitle>
          <DialogContent>
            <UserForm
              editMode={false}
              currentUserName={currentUserName}
              setOpen={setOpenUserForm}
              handleClose={handleCloseUserForm}
              setNewUserId={setNewUserId}
            />
          </DialogContent>
        </Dialog>
      </WidgetWrapper>
    </Box>
  );
}

export default AssesseeTable;
